// GLSL ES 3.00 for the WebGL2 tier: a raymarched star-shaped SDF.
//
// Star-shaped means the radial displacement is a function of DIRECTION only, so
// the surface provably lies inside the shell [r0 - amp, r0 + amp]. Both shell
// spheres are intersected analytically, which turns the march into a fixed
// number of steps with a guaranteed hit or miss — no Lipschitz bound to guess
// and no missed-surface case to paper over.

// mod289, permute and taylorInvSqrt are declared HERE, once, ahead of the noise
// bodies. Ashima and McEwan's noise2D.glsl and noise3D.glsl each ship their own
// copies; pasting more than one of those files into a single shader fails to
// link on the duplicate symbols. Anything added below reuses these.
// Noise is Ashima Arts / Stefan Gustavson, MIT licensed.
const NOISE_GLSL = `
vec3 mod289(vec3 x){ return x - floor(x * (1.0/289.0)) * 289.0; }
vec4 mod289(vec4 x){ return x - floor(x * (1.0/289.0)) * 289.0; }
vec4 permute(vec4 x){ return mod289(((x*34.0)+10.0)*x); }
vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v){
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 =   v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute( permute( permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.5 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 105.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

// Inter-octave rotation kills the axis-aligned grid artefact. Gain 0.45 rather
// than 0.5: at 0.5 too much energy sits in the fine octaves and the surface
// reads as crumpled paper. Normalising by the summed gain keeps the result in
// about [-1,1] so the amplitude uniform stays meaningful at any octave count.
const mat3 M3 = mat3( 0.00, 0.80, 0.60,
                     -0.80, 0.36,-0.48,
                     -0.60,-0.48, 0.64);

float fbm3(vec3 p, int oct){
  float f = 0.0, a = 0.55, n = 0.0;
  for (int i = 0; i < 4; ++i){
    if (i >= oct) break;
    f += a * snoise(p);  n += a;
    p = M3 * p * 2.03;   a *= 0.45;
  }
  return f / max(n, 1e-4);
}

// IQ's canonical domain warp is nested fbm — three fbm evaluations per warp
// level, far too expensive inside a march. Vector-valued single-octave noise
// buys about 90% of the look for three snoise.
vec3 vnoise3(vec3 p){
  return vec3(snoise(p),
              snoise(p + vec3(5.2, 1.3, 7.1)),
              snoise(p + vec3(9.7, 7.4, 3.5)));
}
`;

const FIELDS_GLSL = `
mat3 rotY(float a){ float c = cos(a), s = sin(a); return mat3(c,0,-s, 0,1,0, s,0,c); }

// SILHOUETTE field: low frequency, gentle warp. Warping sharpens gradients, and
// on an outline sharp gradients read as spikes — strong warp here turns the
// character into a virus. The outline stays smooth; all the interest is inside.
float shapeSil(vec3 dir){
  vec3 p = (rotY(uTime * uSpin) * dir) * uFreq + uOrigin;
  vec3 w = vnoise3(p * 0.50 + vec3(0.0, 0.0, uTime * 0.12));
  return fbm3(p + uWarpS * w + vec3(0.0, 0.0, uTime * uFlow), uSOct);
}

float map(vec3 p){
  float r = length(p);
  return r - (uR0 + uAmp * shapeSil(p / max(r, 1e-6)));
}

// DETAIL field: high frequency, strong warp, evaluated in 3D. Two octaves, not
// one: the iso-lines of a single warped octave form concentric rings, the most
// recognisable tell of a cheap noise shader.
float dn(vec3 b){ return 0.66 * snoise(b) + 0.34 * snoise(M3 * b * 2.03); }

// Returns the field value through the out parameter and the gradient as the
// result. The warp vector is computed once and shared by all four taps: 7
// snoise instead of 16.
vec3 detailGrad(vec3 p, out float v){
  vec3 q = (rotY(uTime * uSpin * 1.4) * p) * uDFreq + uOrigin;
  vec3 w = vnoise3(q * 0.45 + vec3(0.0, 0.0, uTime * 0.19)) * uWarp;
  vec3 base = q + w + vec3(0.0, 0.0, uTime * uFlow * 1.7);
  const float e = 0.12;
  float n0 = dn(base);
  v = n0;
  return vec3(dn(base + vec3(e,0.0,0.0)) - n0,
              dn(base + vec3(0.0,e,0.0)) - n0,
              dn(base + vec3(0.0,0.0,e)) - n0) / e;
}

vec3 calcNormal(vec3 p){
  const vec2 k = vec2(1.0, -1.0);
  const float e = 0.0035;
  return normalize( k.xyy * map(p + k.xyy*e) + k.yyx * map(p + k.yyx*e) +
                    k.yxy * map(p + k.yxy*e) + k.xxx * map(p + k.xxx*e) );
}

vec3 palette(float t){ return uPA + uPB * cos(6.283185307 * (uPC * t + uPD)); }

// Narkowicz 2015 ACES filmic approximation. The CPU side mirrors this exactly so
// the 2D and CSS tiers land on the same colours.
vec3 aces(vec3 x){
  return clamp((x*(2.51*x+0.03)) / (x*(2.43*x+0.59)+0.14), 0.0, 1.0);
}

// Interleaved gradient noise, Jimenez 2014.
float ign(vec2 p){
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}
`;

const MAIN_GLSL = `
void main(){
  vec2 uv  = (2.0 * gl_FragCoord.xy - uRes) / min(uRes.x, uRes.y);
  float rn = ign(gl_FragCoord.xy + fract(uTime) * 37.0);

  vec3 ro = vec3(0.0, 0.0, -3.5);
  vec3 rd = normalize(vec3(uv * uZoom, 1.0));

  float bq = dot(ro, rd);
  float cq = dot(ro, ro);
  float dmin = sqrt(max(cq - bq*bq, 0.0));
  vec3  pmin = ro + rd * max(-bq, 0.0);

  // The analytic silhouette pays for itself twice: it antialiases the edge with
  // no supersampling, and it lets most pixels skip the march entirely.
  vec3  dirm  = normalize(pmin + vec3(0.0, 0.0, 1e-4));
  float rSurf = uR0 + uAmp * shapeSil(dirm);
  float sd    = dmin - rSurf;
  float aa    = max(fwidth(sd), 1e-4);
  float cov   = 1.0 - smoothstep(-aa, aa, sd);

  vec3 col = vec3(0.0);

  // Contact edge only. This design language forbids a glow, so there is no wide
  // bloom: the term below falls to nothing within about 3% of the radius and
  // exists so the silhouette does not read as die-cut against the page.
  float ge = max(sd, 0.0) / max(uR0, 1e-4);
  col += uAccent * exp(-90.0 * ge) * (0.0025 + 0.0060 * uLevel);

  // Listening ring: one hairline at 1.06r that tracks the same loudness the body
  // does. fwidth(dmin) is one device pixel expressed in world units.
  if (uRingAmp > 1e-6){
    float rw = max(fwidth(dmin), 1e-5);
    col += uAccent * uRingAmp * (1.0 - smoothstep(0.0, rw * 1.5, abs(dmin - uRingR)));
  }

  if (cov > 0.001){
    float rmax = uR0 + uAmp * 1.02;
    float rmin = max(uR0 - uAmp * 1.02, 1e-3);
    float h = bq*bq - (cq - rmax*rmax);
    if (h > 0.0){
      h = sqrt(h);
      float tEnter = max(-bq - h, 0.0);
      float tExit  = -bq + h;
      float hi = bq*bq - (cq - rmin*rmin);
      // A ray that clears the inner sphere is provably inside the surface by
      // tStop, so a hit is guaranteed and there is no missed-surface case.
      float tStop = (hi > 0.0) ? (-bq - sqrt(hi)) : tExit;
      tStop = max(tStop, tEnter + 1e-4);

      float dt = (tStop - tEnter) / float(uSteps);
      float tPrev = tEnter, tHit = -1.0;
      for (int i = 1; i <= 32; ++i){
        if (i > uSteps) break;
        // Jittering the start with per-pixel IGN is what removes the concentric
        // contour banding a fixed-step march otherwise leaves on the surface.
        float tc = tEnter + dt * (float(i) - rn);
        if (map(ro + rd * tc) < 0.0){ tHit = tc; break; }
        tPrev = tc;
      }
      if (tHit < 0.0 && hi > 0.0) tHit = tStop;

      if (tHit > 0.0){
        float a = tPrev, b = tHit;
        for (int k = 0; k < 4; ++k){
          float m = 0.5 * (a + b);
          if (map(ro + rd * m) < 0.0) b = m; else a = m;
        }
        vec3 p = ro + rd * (0.5 * (a + b));
        vec3 n = calcNormal(p);

        // Bump with the TANGENTIAL part of the detail gradient. All of the
        // interior comes from here, and because it is tangential it costs
        // nothing on the silhouette.
        float nz;
        vec3  g = detailGrad(p, nz);
        g -= n * dot(g, n);
        n = normalize(n - uDetail * g);

        vec3 L1 = normalize(vec3(-0.45, 0.75, -0.60));
        vec3 L2 = normalize(vec3( 0.70, -0.25,  0.35));
        vec3 V  = -rd;
        float ndv  = clamp(dot(n, V), 0.0, 1.0);
        float fres = pow(1.0 - ndv, 3.0);

        // Half-lambert wrap, never a hard terminator: this is a body, not a ball.
        float wrapK = pow(0.5 + 0.5 * dot(n, L1), 1.7);
        float wrap2 = pow(0.5 + 0.5 * dot(n, L2), 2.2);

        float thick = 0.0;
        for (int i = 1; i <= 3; ++i)
          thick += clamp(-map(p + L1 * (0.20 * float(i))) * 3.0, 0.0, 1.0);
        thick /= 3.0;

        // One palette sample, one hue. The research shader offset the red and
        // blue taps for dispersion; that is chromatic aberration and this
        // product is explicitly the opposite of that look.
        float pt   = 0.62 + 0.06*nz + 0.11*fres - 0.09*thick;
        vec3  body = palette(pt);

        // Absorption derived FROM the palette: a medium absorbs the channels it
        // is not. A hard-coded sigma only ever suits one palette and turns the
        // warm state into skin. 0.55 is well below the 2.6 a saturated glass
        // look wants: at 2.6 amber loses its green and blue outright and comes
        // out as a traffic cone, which is the opposite of a rationed accent.
        vec3 bodyN = body / max(max(body.r, max(body.g, body.b)), 1e-3);
        vec3 sigma = (1.0 - bodyN) * 0.55 + 0.35;

        vec3 sss   = exp(-thick * sigma * 1.5);
        // Optical path along the VIEW ray. This single term is what makes a
        // sphere read as glass: a long path through the middle gives a deep
        // saturated core, a short path at the edge lights the body from inside
        // exactly at the silhouette.
        vec3 trans = exp(-ndv * sigma);

        // Keep the body dark and let only the rim, the specular and the veins be
        // bright. High dynamic range with a low mean is what reads as premium;
        // an evenly lit body always reads as a plastic loading spinner.
        vec3 lit = vec3(0.0);
        lit += body * trans * (0.52 + 0.30 * smoothstep(-0.25, 0.90, nz));
        lit += body * wrapK * 0.42;
        lit += body * wrap2 * 0.08;
        lit += body * sss   * 0.16 * (0.55 + 0.30 * uLevel);

        vec3 hv = normalize(L1 + V);
        float ndh = max(dot(n, hv), 0.0);
        lit += uAccent * pow(ndh, 220.0) * 0.24;
        lit += uAccent * pow(ndh,  16.0) * 0.030;
        lit += uAccent * fres * (0.12 + 0.12 * uLevel);

        col = mix(col, col * 0.25 + lit, cov);
      }
    }
  }

  // Exposure. Low enough that the body sits well under the rim: high dynamic
  // range with a low mean is what reads as a considered object, and an evenly
  // lit body always reads as a plastic loading spinner.
  col = aces(col * 0.62);
  col = pow(max(col, 0.0), vec3(1.0/2.2));

  // Triangular-PDF dither at about 1 LSB. Without it the edge falloff bands.
  col += (ign(gl_FragCoord.xy) + ign(gl_FragCoord.xy + 91.7) - 1.0) / 255.0;

  // Premultiplied, matching premultipliedAlpha:true on the context. Alpha is
  // taken from the ENCODED colour so the two stay consistent; deriving it from
  // linear light composites the edge as a grey box.
  fragColor = vec4(col, clamp(cov + dot(col, vec3(0.33)) * 1.4, 0.0, 1.0));
}
`;

// `#version 300 es` must be the very first characters of the source. A leading
// newline from a template literal is the most common WebGL2 compile failure, so
// the literal opens on the backtick line — do not reformat this.
export const VERT_SRC = `#version 300 es
void main(){
  // Full-screen triangle from gl_VertexID: no VBO, no attributes. A VAO must
  // still be bound before the draw call or Chrome errors.
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

export const FRAG_SRC = `#version 300 es
precision highp float;
out vec4 fragColor;

uniform vec2  uRes;
uniform float uTime, uLevel;
uniform int   uSOct, uSteps;
uniform float uR0, uAmp, uFreq, uWarp, uWarpS, uDFreq, uDetail, uFlow, uSpin, uZoom;
uniform float uRingR, uRingAmp;
uniform vec3  uOrigin;
uniform vec3  uPA, uPB, uPC, uPD;
uniform vec3  uAccent;
${NOISE_GLSL}${FIELDS_GLSL}${MAIN_GLSL}`;

export const UNIFORM_NAMES = [
  "uRes",
  "uTime",
  "uLevel",
  "uSOct",
  "uSteps",
  "uR0",
  "uAmp",
  "uFreq",
  "uWarp",
  "uWarpS",
  "uDFreq",
  "uDetail",
  "uFlow",
  "uSpin",
  "uZoom",
  "uRingR",
  "uRingAmp",
  "uOrigin",
  "uPA",
  "uPB",
  "uPC",
  "uPD",
  "uAccent",
] as const;

export type UniformName = (typeof UNIFORM_NAMES)[number];

/**
 * Camera framing. With ro at z=-3.5 the screen radius of a world radius R is
 * R / (uZoom * sqrt(12.25 - R^2)); at uZoom 0.235 the resting body (R = 0.66)
 * fills 82% of the short axis and the loudest frame still clears the edge.
 */
export const ZOOM = 0.235;
